import { observable, computed, when, makeObservable, actionBound } from "mobx";
import userApi from "@/services/api/user";
import securityApi from "@/services/api/security";
import shopApi, { batchGetShop, ShopAppVersionInfo, syncShopSubscription } from "@/services/api/shop";
import SecurityUserDTO from "@/dto/user/SecutityUserDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import UserDTO from "@/dto/user/UserDTO";
import WaybillPrinterLogStore from "./WaybillPrinterLogStore";
import ShopType from "@/enum/ShopType";
import { chunk, groupBy } from "lodash";
import { axiosGroupByShopType } from "@/services/http/platformServer";
import ShopSettingDTO from "@/dto/shop/ShopSettingDTO";
import AppVersion from "@/enum/AppVersion";
import { Sentry, SentryTracing } from "@/services/sentry";

import ShopAdvancedSettingStore from "./ShopAdvancedSettingStore";
import { buildPddDeviceFingerPrintFetcher } from "@/services/http/PddDeviceFingerPrint/PddDeviceFingerPrintFetcher";
import { EcMarketShopTypes, QNSupportShopTypes, isEcMarketShopExpired, isManualShopExpired } from "@/utils/ShopUtils";
import WaybillPrintStore from "./WaybillPrintStore";
import EwaybillAccountStore from "./EwaybillAccountStore";
import PackListPrintStore from "./PackListPrintStore";
import { concurrentQueue, generateFHYRedirectUrl, openUrl } from "@/utils";
import { History } from "history";
import Authority from "@/enum/Authority";
import { showAuthorityDialog, showQnExpiredModal } from "@/utils/dialog";
import type { ModalStore } from "@/hooks/Modal/ModalStore";
import to from "@/utils/to";
import PremiumFeatureTrialStore from "./PremiumFeatureTrialStore";
import { getVersion } from "@/services/version";
import DouDianSDKStore from "./DouDianSDKStore";
import JdSDKStore from "./JdSDKStore";
import ChannelShopSDKStore from "./ChannelShopSDKStore";
import WarehouseWarningStore from "./WarehouseWarningStore";
import CompanyRoleStore from "./CompanyRoleStore";
import OrderLogisticsWarningStore from "./OrderLogisticsWarningStore";
import AreaStore from "./AreaStore";
import UniversalOrderSettingStore from "@/store/UniversalOrderSettingStore";
import { CustomizationShopApprovalStatus, getCustomizationShopList } from "@/services/api/customization";
import AppSubscriptionStore from "./AppSubscriptionStore";
import AuthTokenHolder from "@/services/http/AuthTokenHolder";
import { FD_BASE_URL } from "@/config";

class Store {
    constructor() {
        makeObservable(this, {
            isInit: observable,
            shops: observable,
            fromAdmin: observable,
            newFeature: observable,
            fromIframe: observable,
            rtdwWhiteList: computed,
            allowedShops: computed,
            allowedShopIds: computed,
            shopMapping: computed,
            shopSettingMapping: observable,
            shopSettingLoadingMapping: observable,
            shopSettingLoadErrorMapping: observable,
            reSyncManualShopSubscriptionEndTime: actionBound,
            getShopSetting: actionBound,
            ensureShopSetting: actionBound,
            updateLastSyncOrderTime: actionBound,
            updateLastSyncRefundTime: actionBound,
            shopAppVersionMapping: observable,
            shopAppVersionLoadingMapping: observable,
            getShopAppVersion: actionBound,
            getAllShopAppVersionLoading: observable,
            getAllShopAppVersion: actionBound,
            users: observable,
            currentUser: observable,
            companyId: computed,
            currentUserShop: computed,
            showDecryptMenu: observable,
            setShowDecryptMenu: actionBound,
            masterShop: computed,
            loadShopList: actionBound,
            loadCurrentUser: actionBound,
            isIframe: observable,
            loadCommonData: actionBound,
            showCustomizationMenu: observable,
            getCustomizationV1: actionBound,
            loadAllUsers: actionBound,
            onStartPddDeviceFingerPrintFetcherTask: actionBound,
            checkUserAuthority: actionBound,
            checkManualShopExpired: actionBound,
            batchCheckManualShopExpired: actionBound,
            updateShopSettingMapping: actionBound,
            syncAllOwnerShopSubscription: actionBound,
        });
        this.shopAdvancedSettingStore = new ShopAdvancedSettingStore();
        this.waybillPrintStore = new WaybillPrintStore();
        this.ewaybillAccountStore = new EwaybillAccountStore();
        this.packListPrintStore = new PackListPrintStore();
        this.premiumFeatureTrialStore = new PremiumFeatureTrialStore(this);
        this.DouDianSDKStore = new DouDianSDKStore(this);
        this.JdSDKStore = new JdSDKStore(this);
        this.ChannelShopSDKStore = new ChannelShopSDKStore(this);
        this.warehouseWarningStore = new WarehouseWarningStore();
        this.companyRoleStore = new CompanyRoleStore(this);
        this.areaStore = new AreaStore();
        this.orderSettingStore = new UniversalOrderSettingStore(this);
        this.appSubscriptionStore = new AppSubscriptionStore(this);
    }

    modalStore: ModalStore;

    history: History;

    shopAdvancedSettingStore: ShopAdvancedSettingStore;

    waybillPrintStore: WaybillPrintStore;

    ewaybillAccountStore: EwaybillAccountStore;

    packListPrintStore: PackListPrintStore;

    premiumFeatureTrialStore: PremiumFeatureTrialStore;

    DouDianSDKStore: DouDianSDKStore;

    JdSDKStore: JdSDKStore;

    ChannelShopSDKStore: ChannelShopSDKStore;

    warehouseWarningStore: WarehouseWarningStore;

    companyRoleStore: CompanyRoleStore;

    orderLogisticsWarningStore: OrderLogisticsWarningStore;

    areaStore: AreaStore;

    orderSettingStore: UniversalOrderSettingStore;

    appSubscriptionStore: AppSubscriptionStore;

    /**是否初始化完成 */
    isInit = false;

    // 店铺列表
    shops: ShopDTO[] = [];

    /** Admin进入发货易不允许同步订单的操作 */
    fromAdmin = false;

    newFeature = false;

    fromIframe = false;

    get rtdwWhiteList() {
        return false;

        if (this.companyId === 162105) return true;

        if (this.companyId % 100 < 30) {
            return true;
        }

        return false;
    }

    /**
     * 当前登录用户允许访问的店铺列表
     */
    get allowedShops() {
        const currentShopId = this.currentUser?.shopId;

        return this.shops?.filter((shop) => {
            if (shop.allowedShopIds?.length) {
                return shop.allowedShopIds.includes(currentShopId);
            }

            return true;
        });
    }

    get allowedShopIds() {
        return (this.allowedShops || []).map(({ id }) => id);
    }

    /**
     * 店铺mapping id -> main节点的店铺对象
     */
    get shopMapping() {
        const mapping = new Map<number, ShopDTO>();

        for (let i = 0; i < this.shops?.length; i++) {
            const shop = this.shops[i];

            mapping.set(shop.id, shop);
        }

        return mapping;
    }

    shopSettingMapping = new Map<number, ShopSettingDTO>();

    shopSettingLoadingMapping = new Map<number, boolean>();

    shopSettingLoadErrorMapping = new Map<number, any>();

    /**
     * 手工店铺过期时，前端再去获取一次到期时间
     * 内部已处理异常
     */
    async reSyncManualShopSubscriptionEndTime(expiredShopSetting: ShopSettingDTO) {
        const shopId = expiredShopSetting.id;
        const [error1] = await to(syncShopSubscription(shopId));

        if (error1) return expiredShopSetting;

        const [error2, shopSettings] = await batchGetShop([shopId]);

        if (error2) return expiredShopSetting;

        const latestShopSetting = shopSettings[0];

        if (!latestShopSetting) return expiredShopSetting;

        return latestShopSetting;
    }

    async getShopSetting(shopIds: number[]) {
        const groupedShopIds = groupBy<number>([...shopIds], (shopId) => {
            const shopType = this.shopMapping.get(shopId)!.type;

            return axiosGroupByShopType(shopType);
        });

        // 按节点分组的店铺列表
        const shopIdsList = Object.values(groupedShopIds);

        await concurrentQueue({
            data: shopIdsList,
            callback: async (shopIds) => {
                const loading = shopIds.find((shopId) => this.shopSettingLoadingMapping.get(shopId));

                if (loading) {
                    await when(() => !shopIds.find((shopId) => this.shopSettingLoadingMapping.get(shopId)));

                    shopIds = shopIds.filter((shopId) => {
                        return !this.shopSettingMapping.get(shopId);
                    });

                    if (!shopIds.length) {
                        return;
                    }
                }

                shopIds.forEach((shopId) => {
                    this.shopSettingLoadingMapping.set(shopId, true);
                });

                const shopIdChunks = chunk(shopIds, 10);

                for (const shopIdChunk of shopIdChunks) {
                    try {
                        const shopSettings = await batchGetShop(shopIdChunk);
                        for (let i = 0; i < shopSettings?.length; i++) {
                            let shopSetting = shopSettings[i];
                            if (shopSetting && EcMarketShopTypes.includes(shopSetting.type)) {
                                shopSetting.enableAutoSyncOrder = isEcMarketShopExpired(shopSetting)
                                    ? false
                                    : shopSetting.enableAutoSyncOrder;
                            }

                            if (isManualShopExpired(shopSetting)) {
                                shopSetting = await this.reSyncManualShopSubscriptionEndTime(shopSetting);
                            }

                            this.shopSettingMapping.set(shopSetting.id, shopSetting);
                            this.shopSettingLoadErrorMapping.set(shopSetting.id, undefined);

                            // 如果已得到是高级版，则提前写入到缓存
                            if (shopSetting?.appVersion === AppVersion.PREMIUM) {
                                this.shopAppVersionMapping.set(shopSetting.id, {
                                    id: shopSetting.id,
                                    appVersion: shopSetting.appVersion,
                                });
                            }
                        }
                    } catch (error) {
                        shopIdChunk.forEach((shopId) => {
                            this.shopSettingLoadErrorMapping.set(shopId, error);
                        });

                        throw error;
                    } finally {
                        shopIdChunk.forEach((shopId) => {
                            this.shopSettingLoadingMapping.set(shopId, false);
                        });
                    }
                }
            },
        });
    }

    async ensureShopSetting(shopIds = this.allowedShopIds) {
        const needToLoadShopIds: number[] = [];
        for (let i = 0; i < shopIds.length; i++) {
            const shopId = shopIds[i];

            let shopSetting = this.shopSettingMapping.get(shopId);

            if (!shopSetting) {
                needToLoadShopIds.push(shopId);
            }
        }

        if (needToLoadShopIds.length) {
            await this.getShopSetting(needToLoadShopIds);
        }
    }

    async updateLastSyncOrderTime(shopId: number, lastSyncOrderTime: string) {
        const shopSetting = this.shopSettingMapping.get(shopId);

        if (shopSetting) {
            shopSetting.lastSyncOrderTime = lastSyncOrderTime;
        }
    }

    async updateLastSyncRefundTime(shopId: number, lastSyncRefundTime: string) {
        const shopSetting = this.shopSettingMapping.get(shopId);

        if (shopSetting) {
            shopSetting.lastSyncRefundTime = lastSyncRefundTime;
        }
    }

    shopAppVersionMapping = new Map<number, ShopAppVersionInfo>();

    shopAppVersionLoadingMapping = new Map<number, boolean>();

    /**
     * 加载店铺版本
     */
    async getShopAppVersion(shopId: number, isReload = false) {
        const loading = this.shopAppVersionLoadingMapping.get(shopId);

        if (loading) {
            await when(() => !this.shopAppVersionLoadingMapping.get(shopId));

            return this.shopAppVersionMapping.get(shopId);
        }

        const cache = this.shopAppVersionMapping.get(shopId);

        if (cache && !isReload) {
            return cache;
        }

        this.shopAppVersionLoadingMapping.set(shopId, true);

        try {
            await this.getShopSetting([shopId]);

            const shopSetting = this.shopSettingMapping.get(shopId);

            let info: ShopAppVersionInfo = {
                id: shopId,
                appVersion: shopSetting?.appVersion,
            };

            this.shopAppVersionMapping.set(info.id, info);

            return info;
        } finally {
            this.shopAppVersionLoadingMapping.set(shopId, false);
        }
    }

    getAllShopAppVersionLoading = false;

    /**
     * 批量加载店铺版本
     */
    async getAllShopAppVersion() {
        this.getAllShopAppVersionLoading = true;

        try {
            const needLoadShops = this.allowedShops.filter((it) => {
                const info = this.shopAppVersionMapping.get(it.id);

                if (info?.appVersion === AppVersion.PREMIUM) {
                    return false;
                }

                return true;
            });

            const shopGroupByShopType = groupBy(needLoadShops, (shop) => axiosGroupByShopType(shop.type));

            const groupShops = Object.values(shopGroupByShopType);

            const allShopDetails: ShopSettingDTO[] = [];

            for (let i = 0; i < groupShops?.length; i++) {
                const shops = groupShops?.[i];

                const shopIds = shops?.map((it) => it.id);

                if (shopIds?.length) {
                    const shopDetails = await batchGetShop(shops.map((it) => it.id));

                    if (shopDetails) {
                        allShopDetails.push(...shopDetails);
                    }
                }
            }

            allShopDetails.forEach((shop) => {
                this.shopAppVersionMapping.set(shop.id, {
                    id: shop.id,
                    appVersion: shop.appVersion,
                });
            });
        } catch (error) {
            console.error(error);
        } finally {
            this.getAllShopAppVersionLoading = false;
        }
    }

    // 用户列表
    users: UserDTO[] = [];

    // 当前店铺用户信息
    currentUser: SecurityUserDTO;

    /**当前用户的租户id */
    get companyId() {
        return this.currentUser?.companyId;
    }

    get currentUserShop() {
        return this.shopMapping.get(this.currentUser?.shopId);
    }

    showDecryptMenu = false;

    setShowDecryptMenu(bool: boolean) {
        this.showDecryptMenu = bool;
    }

    /**
     * 主店铺
     */
    get masterShop() {
        return this.shops.find(({ isMasterShop }) => isMasterShop);
    }

    async loadShopList() {
        this.shops = await shopApi.list();
    }

    async loadCurrentUser() {
        this.currentUser = await securityApi.getCurrentUser();
    }

    configSentry() {
        if (import.meta.env.DEV) {
            return;
        }

        try {
            if (!import.meta.env.VITE_IS_QN) {
                Sentry.init({
                    dsn: "https://04be0d4d738b461c85ca84eb1cbdf486@sentry.fahuoyi.com/6",
                    release: getVersion(),
                    integrations: [new SentryTracing.Integrations.BrowserTracing()],
                    tracesSampleRate: 0.05,
                    ignoreErrors: [
                        "店铺授权过期，请重新授权",
                        "请先至少选择一个订单",
                        "Request failed with status code 403",
                        "Non-Error promise rejection captured with keys: errorFields, outOfDate, values",
                        "Unexpected token B in JSON at position 0",
                        "用户取消操作",
                        "ResizeObserver loop",
                        "Non-Error promise rejection captured with value: useRequest has caught the exception",
                        "PRINTER_CHANGED is not defined",
                        "KISSY is not defined",
                        "UnknownError Internal error opening backing store for indexedDB.open",
                        "Unable to find node on an unmounted component",
                        "DatabaseClosedError",
                        "Cannot call hover while not dragging",
                    ],
                });

                Sentry.configureScope((scope) => {
                    scope.setLevel("error");

                    const shopId = this.currentUser.shopId.toString();
                    const shopName = this.shopMapping.get(this.currentUser.shopId)?.name;
                    const username = this.currentUser.username || this.currentUser.nickname;

                    scope.setTag("shopId", shopId);
                    scope.setTag("shopName", shopName);
                    scope.setTag("username", username);
                    scope.setUser({
                        id: this.currentUser.id.toString(),
                        shopId: shopId,
                        shopName: shopName,
                        username: username,
                    });
                });
            }
        } catch (error) {}
    }

    isIframe = false;

    async aliRedirectWeb() {
        try {
            if (import.meta.env.VITE_IS_QN) {
                return;
            }

            if (this.fromIframe) {
                return;
            }

            const hasAli = this.allowedShops.some(({ type }) => type === ShopType.ALIBABA);

            if (!hasAli) return;

            // 在iframe中
            if (window.top !== window.self) {
                const ua = navigator.userAgent; // iframe中使用ua判断

                const match = ua.match(/(?:Chrome|Chromium)\/(\d+)/);

                if (match) {
                    const mainVersion = Number(match[1]);

                    if (!Number.isNaN(mainVersion) && mainVersion >= 147) {
                        this.isIframe = true;
                    }
                }
            }
        } catch {}
    }

    async redirectWeb() {
        if (!import.meta.env.VITE_IS_QN) {
            return;
        }

        // 如果有千牛不支持的店铺
        const hasUnSupport = this.allowedShops.some(({ type }) => !QNSupportShopTypes.includes(type));

        if (!hasUnSupport) {
            return;
        }

        const url = await generateFHYRedirectUrl("/");

        showQnExpiredModal();

        openUrl(url);
    }

    async loadCommonData() {
        this.isInit = false;

        try {
            // 如果是千牛进入,则走同步流程
            if (import.meta.env.VITE_IS_QN) {
                await this.loadShopList();

                await this.loadCurrentUser();

                await this.companyRoleStore.getIsRetailer();
            } else {
                await Promise.all([
                    this.loadShopList(),
                    this.loadCurrentUser(),
                    this.companyRoleStore.getIsRetailer(),
                    this.getCustomizationV1(),
                ]);
            }

            await this.redirectWeb();
            await this.aliRedirectWeb();

            this.configSentry();

            this.loadAllUsers();

            if (this.currentUser.companyId % 10 < 1) {
                new WaybillPrinterLogStore();
            }

            this.onStartPddDeviceFingerPrintFetcherTask();
        } catch (error) {
            // 发生在 token存在时，但是用户重新进入页面时的 token授权过期
            // 跳转回登录页
            const responseStatus = error?.response?.status;
            if (responseStatus === 401 || responseStatus === 403) {
                this.history?.replace("/login");
            }

            throw error;
        } finally {
            this.isInit = true;
        }
    }

    // 是否显示定制菜单导航
    showCustomizationMenu = false;

    async getCustomizationV1() {
        const dataSource = await getCustomizationShopList();

        this.showCustomizationMenu = !!dataSource?.find(
            (it) => it.shopInfo?.approvalStatus === CustomizationShopApprovalStatus.ACCEPTED,
        );
    }

    async loadAllUsers() {
        this.users = await userApi.getAllUsers();
    }

    async onStartPddDeviceFingerPrintFetcherTask() {
        const pddRelatedShops = this.allowedShops.filter(
            (shop) =>
                shop.type === ShopType.PINDUODUO ||
                shop.type === ShopType.PDD_FDS ||
                shop.type === ShopType.PDD_DROP_SHIPPING_SUPPLIER ||
                shop.type === ShopType.PDD_FULFILLMENT,
        );

        if (!pddRelatedShops.length || import.meta.env.VITE_IS_QN) return;

        const instance = buildPddDeviceFingerPrintFetcher(this);

        await instance.startPddDeviceFingerPrintFetcherTask(true, true);
    }

    checkUserAuthority(authority: Authority) {
        const hasAuthority = this.currentUser?.authorities?.includes?.(authority);

        if (!hasAuthority) {
            showAuthorityDialog(this.history, this.currentUser?.username, authority);

            return false;
        }

        return true;
    }

    /**
     * 检查店铺是否过期
     * 目前只检测手工店铺
     */
    async checkManualShopExpired(shopId: number) {
        const shop = this.shopMapping.get(shopId);
        const ownerShop = this.shopMapping.get(shop?.ownerShopId);

        if (shop?.type === ShopType.MANUAL) {
            await this.ensureShopSetting([shop.id]);

            const shopSetting = this.shopSettingMapping.get(shop.id);

            if (isManualShopExpired(shopSetting)) {
                return {
                    isExpired: true,
                    shop,
                    ownerShop,
                };
            }
        }

        return {
            isExpired: false,
            shop,
            ownerShop,
        };
    }

    /**
     * 批量检查店铺是否过期
     * 目前只检测手工店铺，并且只会返回过期的店铺列表信息
     */
    async batchCheckManualShopExpired(shopIds: number[]) {
        const needCheckManualShopIds: number[] = [];

        for (let i = 0; i < shopIds.length; i++) {
            const shopId = shopIds[i];

            const shop = this.shopMapping.get(shopId);

            if (shop?.type === ShopType.MANUAL) {
                needCheckManualShopIds.push(shopId);
            }
        }

        const results: { isExpired: boolean; shop: ShopDTO; ownerShop?: ShopDTO }[] = [];

        if (needCheckManualShopIds.length) {
            await this.ensureShopSetting(needCheckManualShopIds);

            for (let i = 0; i < needCheckManualShopIds.length; i++) {
                const shopId = needCheckManualShopIds[i];
                const shop = this.shopMapping.get(shopId);

                const ownerShop = this.shopMapping.get(shop?.ownerShopId);
                const shopSetting = this.shopSettingMapping.get(shop.id);

                if (isManualShopExpired(shopSetting)) {
                    results.push({
                        isExpired: true,
                        shop,
                        ownerShop,
                    });
                }
            }

            return results;
        } else {
            return results;
        }
    }

    updateShopSettingMapping(shopId: number, setting: Partial<ShopSettingDTO>) {
        const shopSetting = this.shopSettingMapping.get(shopId);

        if (shopSetting) {
            for (const key in setting) {
                shopSetting[key] = setting[key];
            }
        }
    }

    /**
     * 更新店铺订阅
     *
     * 如果有所属店铺，则更新 所属店铺和所有相同所属店铺的到期时间
     *
     * @returns {number[]} 返回的更新店铺的shopId列表
     */
    async syncAllOwnerShopSubscription(shopId: number) {
        const shop = this.shopMapping.get(shopId);

        const ownerShopId = shop?.ownerShopId;

        const needSyncShopSubscriptionShopIds: number[] = [];

        // 如果本身是子店铺，则先找到所属店铺再往下查
        if (ownerShopId) {
            const ownerShop = this.shopMapping.get(ownerShopId);

            if (ownerShop) {
                needSyncShopSubscriptionShopIds.push(ownerShopId);

                this.shops.forEach((shop) => {
                    if (shop.ownerShopId === ownerShopId) {
                        needSyncShopSubscriptionShopIds.push(shop.id);
                    }
                });
            } else {
                needSyncShopSubscriptionShopIds.push(shopId);
            }
        }
        // 如果本身不是子店铺，则向下查所属店铺是该店铺的子店铺
        else {
            needSyncShopSubscriptionShopIds.push(shopId);

            this.shops.forEach((shop) => {
                if (shop.ownerShopId === shopId) {
                    needSyncShopSubscriptionShopIds.push(shop.id);
                }
            });
        }

        for (let i = 0; i < needSyncShopSubscriptionShopIds.length; i++) {
            const shopId = needSyncShopSubscriptionShopIds[i];

            await syncShopSubscription(shopId);
        }

        return needSyncShopSubscriptionShopIds;
    }

    /**
     * 是否仅有下面的店铺类型
     */
    isOnlyHasThisShopTypes(shopTypes: ShopType[]) {
        const hasAnotherShopType = !!this.shops?.some((shop) => {
            return !shopTypes.includes(shop.type);
        });

        if (hasAnotherShopType) {
            return false;
        }

        return true;
    }
}
export default new Store();
